import type { RouteMode } from './route-planner-routing.js';

interface RouteRequest {
  points: Array<{ lat: number; lng: number }>;
  mode: RouteMode;
}
interface RouteAttempt extends RouteRequest {
  sameRoute: boolean;
  hadRoute: boolean;
  startedAt: number;
}
type RouteOutcome<Result> = RouteAttempt & ({ result: Result; error?: never } | { result?: never; error: unknown });
type CalculationState =
  | { status: 'idle' }
  | { status: 'starting'; attempt: RouteAttempt }
  | { status: 'scheduled'; attempt: RouteAttempt; timer: ReturnType<typeof setTimeout> }
  | { status: 'loading'; attempt: RouteAttempt; controller: AbortController };

/** Own cancellation and snapshots so a late provider reply cannot replace a newer edit. */
export function createRouteCalculation<Result>({ load, start, settle }: {
  load(points: RouteRequest['points'], mode: RouteMode, signal: AbortSignal): Promise<Result>;
  start(attempt: RouteAttempt): void;
  settle(outcome: RouteOutcome<Result>): void;
}) {
  let state: CalculationState = { status: 'idle' };
  let successfulKey: string | undefined;
  // Keep completed routes in this editor only; never persist private stop coordinates.
  const cache = new Map<string, { result: Result; expires: number }>();

  function cancel() {
    const previous = state;
    state = { status: 'idle' };
    if (previous.status === 'loading') previous.controller.abort();
    if (previous.status === 'scheduled') clearTimeout(previous.timer);
  }

  async function run(attempt: RouteAttempt, key: string) {
    if (state.status === 'idle' || state.attempt !== attempt) return;
    const active = new AbortController();
    const loading: CalculationState = { status: 'loading', attempt, controller: active };
    state = loading;
    attempt.startedAt = performance.now();
    const current = () => state === loading && !active.signal.aborted;
    let outcome: RouteOutcome<Result>;
    try {
      const cached = cache.get(key);
      const result = cached && cached.expires > Date.now()
        ? structuredClone(cached.result)
        : await load(attempt.points, attempt.mode, active.signal);
      if (!current()) return;
      if (result !== null) {
        successfulKey = key;
        if (!cached || cached.expires <= Date.now()) {
          cache.delete(key);
          cache.set(key, { result: structuredClone(result), expires: Date.now() + 5 * 60_000 });
          if (cache.size > 20) cache.delete(cache.keys().next().value!);
        }
      }
      outcome = { ...attempt, result };
    } catch (error) {
      if (!current() || (error instanceof DOMException && error.name === 'AbortError')) return;
      outcome = { ...attempt, error };
    } finally {
      if (state === loading) state = { status: 'idle' };
    }
    settle(outcome);
  }

  return {
    cancel,
    schedule(points: RouteRequest['points'], mode: RouteMode, delay = 0) {
      cancel();
      const request = { points: points.map(({ lat, lng }) => ({ lat, lng })), mode };
      const key = JSON.stringify(request);
      const attempt = { ...request, sameRoute: key === successfulKey, hadRoute: successfulKey !== undefined, startedAt: performance.now() };
      const starting: CalculationState = { status: 'starting', attempt };
      state = starting;
      start(attempt);
      if (state !== starting) return;
      if (delay && (cache.get(key)?.expires ?? 0) <= Date.now()) {
        state = { status: 'scheduled', attempt, timer: setTimeout(() => { void run(attempt, key); }, delay) };
      } else void run(attempt, key);
    },
  };
}
